import { FC } from 'react';
import { Box, Button, ChakraProps, Flex, Grid } from '@chakra-ui/react';
import { ValidationErrors } from 'final-form';
import { Form as FormProps, FormField as FormFieldProps } from '../../types';
import Form, { FormField } from '../Form';
import FormLegalLinks from './FormLegalLinks';
import { validateFieldByType } from '../../lib/utils.validate';
import { useTranslation } from '../../lib/hooks.context';

const validateField = ({
  name,
  value,
  values,
}: {
  name: string;
  value: string | string[];
  values: Record<string, unknown>;
}): string | undefined | Record<string, string> => {
  switch (name) {
    case 'haluan_yhteydenoton': {
      // value is also a name of another field, which gives necessary info to fulfil the request to contact
      const flatValue = Array.isArray(value) ? value[0] : value;
      return !flatValue ||
        (typeof values[flatValue] === 'string' &&
          (values[flatValue] as string).length)
        ? undefined
        : { [flatValue]: 'Pakollinen' };
    }
    default:
      return undefined;
  }
};

const FeedbackForm: FC<FormProps & ChakraProps> = ({
  fields,
  children,
  legal,
  ...rest
}) => {
  const t = useTranslation();
  const onValidate = (values = {}) => {
    const errors: ValidationErrors = fields.reduce(
      (acc, { required, name, type }) => {
        const fieldValidation = validateField({
          name,
          value: values[name],
          values,
        });
        if (typeof fieldValidation === 'object') {
          return {
            ...acc,
            ...fieldValidation,
            [name]: validateFieldByType({
              required,
              type,
              value: values[name],
            }),
          };
        }
        return {
          ...acc,
          [name]:
            validateFieldByType({ required, type, value: values[name] }) ||
            fieldValidation,
        };
      },
      {},
    );

    return errors;
  };
  const [firstFields, laterFields] = fields.reduce<FormFieldProps[][]>(
    (acc, field, i) =>
      i < 4
        ? [[...(acc[0] || []), field]]
        : [acc[0], [...(acc[1] || []), field]],
    [],
  );

  return (
    <Form py="8" validate={onValidate} {...rest}>
      {({ submitting }) => (
        <>
          {children}
          <Grid
            gridTemplateColumns="repeat(auto-fit, minmax(420px, 1fr))"
            gridColumnGap="6"
            gridRowGap="1"
            mb="8"
          >
            <Box>
              {firstFields.map((field) => (
                <FormField key={field.name} {...field} />
              ))}
            </Box>
            <Box>
              {laterFields.map((field) => (
                <FormField key={field.name} {...field} />
              ))}
            </Box>
          </Grid>
          <Box pt="2">
            <Button
              type="submit"
              variant="solidGreen"
              disabled={submitting}
              isLoading={submitting}
              loadingText={t('button_submitting')}
              size="large"
            >
              {t('button_submit')}
            </Button>
          </Box>
          {legal && legal.length > 0 ? <FormLegalLinks legal={legal} /> : null}
        </>
      )}
    </Form>
  );
};

export default FeedbackForm;
